// ConsumableLots.jsx: Welding consumables register (2026-09-29).
// Every consumable lot (wire, rod, flux, gas) from GET /api/consumable-lots
// (tenant scoped): CoC number, verification, quantity remaining. Records only.

import React, { useEffect, useMemo, useState } from "react";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import ApiCall from "../../utils/apicall";
import { StatusPill } from "../../components/WeldAssure";
import RegisterShell, { fmtDate } from "./RegisterShell";

const kindFor = (s) => (s === "VERIFIED" || s === "RELEASED" ? "success" : s === "AT_RISK" || s === "QUARANTINE" ? "danger" : "warning");
const TYPES = ["WELDING_WIRE", "ELECTRODE", "FLUX", "GAS"];

export default function ConsumableLots() {
  const [lots, setLots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [type, setType] = useState("all");

  useEffect(() => {
    (async () => {
      try {
        const res = await ApiCall.get("/consumable-lots");
        setLots(res.data?.data?.lots || []);
      } catch (err) { setError(err?.response?.data?.message || "Could not load consumable lots."); }
      finally { setLoading(false); }
    })();
  }, []);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return lots.filter((l) => (type === "all" || l.consumable_type === type)
      && (!q || [l.product_designation, l.supplier, l.coc_number, l.storage_location].some((v) => String(v || "").toLowerCase().includes(q))));
  }, [lots, search, type]);

  const kpis = [
    { label: "LOTS", value: lots.length },
    { label: "COC VERIFIED", value: lots.filter((l) => l.coc_verified_at).length },
    { label: "COC NOT VERIFIED", value: lots.filter((l) => !l.coc_verified_at).length },
    { label: "EMPTY", value: lots.filter((l) => Number(l.quantity_remaining) <= 0).length },
  ];

  return (
    <RegisterShell
      title="Welding Consumables"
      subtitle="Every lot of wire, rod, flux and gas received, with its certificate of conformance."
      guide={{ why: "Know which consumable lots have a verified CoC before they reach a welder.", who: "Receivers, welding supervisors and QC.", what: "Each lot, its classification, supplier, CoC, verification and quantity left.", how: "Filter by type or search by product, supplier or CoC.", when: "At receipt and before issue.", standard: "Welding consumable control and traceability" }}
      kpis={kpis}
      columns={["PRODUCT", "TYPE", "SUPPLIER", "COC", "COC VERIFIED", "REMAINING", "RECEIVED", "STATUS"]}
      rows={rows}
      loading={loading}
      error={error}
      search={search}
      onSearch={setSearch}
      searchPlaceholder="Product, supplier or CoC"
      toolbar={(
        <ToggleButtonGroup size="small" exclusive value={type} onChange={(_, v) => v && setType(v)} aria-label="Filter by type">
          <ToggleButton value="all">All</ToggleButton>
          {TYPES.map((t) => <ToggleButton key={t} value={t}>{t === "WELDING_WIRE" ? "Wire" : t.charAt(0) + t.slice(1).toLowerCase()}</ToggleButton>)}
        </ToggleButtonGroup>
      )}
      emptyText="No consumable lots on file. Lots appear here when they are received."
      renderRow={(l) => (
        <tr key={l.id}>
          <td>{l.product_designation}</td>
          <td>{String(l.consumable_type || "").replace("_", " ").toLowerCase()}</td>
          <td>{l.supplier || ""}</td>
          <td style={{ fontFamily: "monospace" }}>{l.coc_number || ""}</td>
          <td>{l.coc_verified_at ? `${fmtDate(l.coc_verified_at)}, ${l.coc_verified_by_name || "not recorded"}` : "not verified"}</td>
          <td>{Number(l.quantity_remaining ?? 0)} of {Number(l.quantity_received ?? 0)} {l.quantity_unit || ""}</td>
          <td>{fmtDate(l.received_at)}</td>
          <td><StatusPill label={l.status || "not set"} kind={kindFor(l.status)} /></td>
        </tr>
      )}
    />
  );
}
